import type { App } from 'vue'
import posthog, { type CaptureResult } from 'posthog-js'

import { isReloadingForStaleChunk } from '@/utils/reloadOnStaleChunk'

type TelemetryProperties = Record<string, unknown>

interface IdentifyUserPayload {
  username: string
  roles?: string[]
}

function getRuntimeMetadata(): TelemetryProperties {
  if (typeof window === 'undefined') {
    return {
      runtime_host: '',
      runtime_origin: '',
      runtime_path: ''
    }
  }

  return {
    runtime_host: window.location.host,
    runtime_origin: window.location.origin,
    runtime_path: window.location.pathname
  }
}

function getBuildMetadata(): TelemetryProperties {
  return {
    app_version: import.meta.env.VITE_APP_VERSION || '',
    app_commit_hash: import.meta.env.VITE_APP_COMMIT_HASH || '',
    app_commit_date: import.meta.env.VITE_APP_COMMIT_DATE || '',
    app_country: import.meta.env.VITE_COUNTRY || '',
    app_title: import.meta.env.VITE_APP_TITLE || '',
    build_base_api: import.meta.env.VITE_BASE_API || ''
  }
}

function getTelemetryMetadata(): TelemetryProperties {
  return {
    ...getBuildMetadata(),
    ...getRuntimeMetadata()
  }
}

// HTTP failures are reported as `http_request_failed` events (see httpRequestFailure.ts),
// so the SDK's exception autocapture must not report them again as errors.
// Failures caused by the deliberate stale-chunk page reload are expected and not worth reporting either.
function dropExpectedExceptions(captureResult: CaptureResult | null): CaptureResult | null {
  if (captureResult?.event !== '$exception') return captureResult
  if (isReloadingForStaleChunk()) return null

  const exceptionList = captureResult.properties?.$exception_list as Array<{ type?: string }> | undefined

  return exceptionList?.some(exception => exception.type === 'AxiosError') ? null : captureResult
}

export function initPosthog(): void {
  posthog.init(import.meta.env.VITE_POSTHOG_PROJECT_TOKEN || '', {
    api_host: import.meta.env.VITE_POSTHOG_HOST || '/e',
    ui_host: 'https://eu.posthog.com',
    defaults: '2026-01-30',
    capture_exceptions: true,
    disable_session_recording: false,
    person_profiles: 'identified_only',
    before_send: dropExpectedExceptions,
    session_recording: {
      maskAllInputs: true
    }
  })

  posthog.register(getTelemetryMetadata())
}

export function setupVueErrorTracking(app: App): void {
  app.config.errorHandler = (err, instance, info) => {
    captureError(err, {
      source: 'vue',
      component_name: instance?.$options?.name,
      error_info: info
    })
    console.error(err, info)
  }
}

// Usernames are emails (personal data) - send a SHA-256 digest as the distinct id instead.
async function getPseudonymousUserId(username: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(username.trim().toLowerCase()))

  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')
}

export function identifyUser({ username, roles = [] }: IdentifyUserPayload): void {
  getPseudonymousUserId(username)
    .then(userId => {
      posthog.identify(userId, {
        roles,
        ...getTelemetryMetadata()
      })
    })
    .catch(error => {
      console.error('PostHog identify failed', error)
    })
}

export function resetUser(): void {
  posthog.reset()
}

export function captureEvent(eventName: string, properties: TelemetryProperties = {}): void {
  posthog.capture(eventName, {
    ...getTelemetryMetadata(),
    ...properties
  })
}

export function captureError(error: unknown, properties: TelemetryProperties = {}): void {
  posthog.captureException(error, {
    ...getTelemetryMetadata(),
    ...properties
  })
}
